import { queryAll } from './dom.js';

/**
 * Generic horizontal scroll row with a draggable indicator — a thin custom
 * "scrollbar" that also supports click/drag-to-scroll on its track. Used by
 * the add-on page's option rows and the homepage's category/sweet-traditions
 * carousels.
 *
 * Markup contract, per [data-scroll-section]:
 *   [data-scroll-track]                    the scrollable row (overflow-x)
 *   .scroll-carousel__indicator            hidden via .is-hidden when the
 *                                          row fits without scrolling
 *     .scroll-carousel__indicator-track
 *       .scroll-carousel__indicator-marker
 */
export function initScrollCarousels() {
  queryAll('[data-scroll-section]').forEach(section => {
    const group     = section.querySelector('[data-scroll-track]');
    if (!group) return;

    // Trackpad swipes and touch drags scroll the row natively, but a plain
    // mouse drag does not — the browser only scrolls a container from actual
    // wheel/touch gestures. Same fix as the corporate carousel
    // (corporate-carousel.js), simplified: this row has an ordinary bounded
    // scrollLeft range, not an infinite loop, so no wrap/recentre logic.
    group.addEventListener('mousedown', e => {
      e.preventDefault(); // stop native image-drag ghosting and text selection

      const dragStartX      = e.clientX;
      const dragStartScroll = group.scrollLeft;

      function onMove(e) {
        group.scrollLeft = dragStartScroll - (e.clientX - dragStartX);
      }

      function onUp() {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      }

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    const indicator = section.querySelector('.scroll-carousel__indicator');
    const trackEl    = section.querySelector('.scroll-carousel__indicator-track');
    const marker     = section.querySelector('.scroll-carousel__indicator-marker');
    if (!indicator || !trackEl || !marker) return;

    function syncIndicator() {
      const visible = group.clientWidth;
      const total   = group.scrollWidth;

      // .is-hidden uses display:none !important, which overrides display:flex
      if (total <= visible) {
        indicator.classList.add('is-hidden');
        return;
      }

      indicator.classList.remove('is-hidden');

      const trackW  = trackEl.clientWidth;
      const ratio   = visible / total;
      const markerW = Math.round(ratio * trackW);
      const maxLeft = trackW - markerW;
      const scrollR = group.scrollLeft / (total - visible);

      marker.style.width = markerW + 'px';
      marker.style.left  = Math.round(scrollR * maxLeft) + 'px';
    }

    group.addEventListener('scroll', syncIndicator, { passive: true });
    new ResizeObserver(syncIndicator).observe(group);
    syncIndicator();

    // Drag-to-scroll on the track
    trackEl.addEventListener('mousedown', e => {
      e.preventDefault();

      const scrollRange = group.scrollWidth - group.clientWidth;
      if (scrollRange <= 0) return;

      const markerW  = parseFloat(marker.style.width) || 0;
      const maxLeft  = trackEl.clientWidth - markerW;
      if (maxLeft <= 0) return;

      // On initial click, jump the scroll position to where the user clicked,
      // centring the marker on the pointer.
      const rect    = trackEl.getBoundingClientRect();
      const clickX  = e.clientX - rect.left;
      const targetLeft = Math.max(0, Math.min(maxLeft, clickX - markerW / 2));
      group.scrollLeft = (targetLeft / maxLeft) * scrollRange;

      const dragStartX      = e.clientX;
      const dragStartScroll = group.scrollLeft;

      function onMove(e) {
        const dx       = e.clientX - dragStartX;
        const newScroll = dragStartScroll + dx * (scrollRange / maxLeft);
        group.scrollLeft = Math.max(0, Math.min(scrollRange, newScroll));
      }

      function onUp() {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup',   onUp);
      }

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup',   onUp);
    });
  });
}
